import { MaterialCommunityIcons } from "@expo/vector-icons";
import { getOnSiteIssueLabel } from "@taskhealth/shared";
import Constants from "expo-constants";
import { useLocalSearchParams, router } from "expo-router";
import { useState, useMemo, useEffect } from "react";
import { ScrollView, StyleSheet, View, Pressable, Text } from "react-native";
import { Snackbar } from "react-native-paper";
import { SafeAreaView } from "react-native-safe-area-context";

import { OrientationCard } from "@/components/assessment/OrientationCard";
import { ConfirmDialog } from "@/components/ConfirmDialog";
import { AddressSection } from "@/components/visit/AddressSection";
import { AideCard } from "@/components/visit/AideCard";
import { AssessmentButton } from "@/components/visit/AssessmentButton";
import { ClockInModal } from "@/components/visit/ClockInModal";
import { ClockOutModal, type FinishPayload } from "@/components/visit/ClockOutModal";
import { CorrectionQuestionsBadge } from "@/components/visit/CorrectionQuestionsBadge";
import { LiveTranslationButton } from "@/components/visit/LiveTranslationButton";
import { MedicalInfoCard } from "@/components/visit/MedicalInfoCard";
import { PatientContacts } from "@/components/visit/PatientContacts";
import { PatientContextCard } from "@/components/visit/PatientContextCard";
import { ReportIssueModal } from "@/components/visit/ReportIssueModal";
import { StickyBottomButton } from "@/components/visit/StickyBottomButton";
import { TimeClockPayment } from "@/components/visit/TimeClockPayment";
import { VisitDocumentsCard } from "@/components/visit/VisitDocumentsCard";
import { VisitHeader } from "@/components/visit/VisitHeader";
import { useConfirmDialog } from "@/hooks/useConfirmDialog";
import { useVisitContext } from "@/hooks/useVisitContext";
import { isAideEvaluationVisit } from "@/lib/aide-contact";
import {
    clockIn,
    clockOut,
    extractOpenVisitConflict,
    submitAndClockOut,
    reportVisitIssue,
    resumeVisitIssue,
    type ReportIssueReason,
    type Section
} from "@/lib/api";
import type { CaptureQuality } from "@/lib/capture-location";
import { useFormContext } from "@/lib/FormContext";
import { Recorder } from "@/lib/recorder";
import { colors, fonts, spacing, radius, missedVisitColors } from "@/lib/theme";
import { fetchRnTickets } from "@/lib/ticket-api";
import { VisitLocationTrail } from "@/lib/visit-location-trail";
import {
    computeVisitState,
    isClockedOutPaperworkOpen,
    isVisitCaptureLive
} from "@/lib/visit-state";

function computeAge(dob: string | null): number | null {
    if (!dob) {
        return null;
    }
    // Parse manually — never use new Date(dateString) (CLAUDE.md timezone rule)
    const [y, m, d] = dob.split("-").map(Number);
    if (!y || !m || !d) {
        return null;
    }
    const today = new Date();
    let age = today.getFullYear() - y;
    const monthDiff = today.getMonth() + 1 - m;
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < d)) {
        age--;
    }
    return age;
}

export default function VisitDetailScreen() {
    // `reportIssue`: set by the supervisory form when she says the aide isn't
    // there (D6a); opens the Report an issue sheet on that reason.
    const { id, reportIssue } = useLocalSearchParams<{ id: string; reportIssue?: string }>();
    const { form, updateForm } = useFormContext();
    const [clockInModalVisible, setClockInModalVisible] = useState(false);
    const [clockOutModalVisible, setClockOutModalVisible] = useState(false);
    const [reportIssueModalVisible, setReportIssueModalVisible] = useState(false);
    const [reportIssueInitialReason, setReportIssueInitialReason] =
        useState<ReportIssueReason | null>(null);
    const [issueTicketId, setIssueTicketId] = useState<number | null>(null);
    const [actionLoading, setActionLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const { dialogState, showDialog, dismissDialog } = useConfirmDialog();

    const { visit, patient, phases, sectionStatuses } = form;
    const isOpenForResubmission = visit.openForResubmission === true;
    const visitState = computeVisitState(visit);
    // Patient context — "before you go in". Live before and after clock-in:
    // the hints are rebuilt on the server whenever the patient's context
    // changes, and the hook re-reads while a rebuild is on its way.
    const { context: patientContext } = useVisitContext(id, visitState === 1 || visitState === 2);
    // Supervisory / performance-evaluation visits: the nurse is here for the
    // aide, so the aide gets a card of her own (name + phone) and drops out of
    // the generic contacts list.
    const evaluatesAide = isAideEvaluationVisit(phases);

    // Compute progress
    const isSectionVisible = (s: Section) => s.visibleOnMobile !== false;
    const allSections: Section[] = phases.flatMap((p) => p.sections.filter(isSectionVisible));
    const completedSections = allSections.filter((s) => {
        const status = sectionStatuses.find((ss) => String(ss.sectionId) === String(s.id));
        return status?.status === "completed";
    }).length;
    const totalSections = allSections.length;

    const completedPhases = useMemo(() => {
        return phases.filter((phase) => {
            const visibleSections = phase.sections.filter(isSectionVisible);
            if (visibleSections.length === 0) {
                return false;
            }
            return visibleSections.every((s) => {
                const status = sectionStatuses.find((ss) => String(ss.sectionId) === String(s.id));
                return status?.status === "completed";
            });
        }).length;
    }, [phases, sectionStatuses]);

    type ClockLocation = {
        // lat/lng are optional: a no-GPS clock-in proceeds with just a
        // locationNote reason (see ClockInModal's "no GPS" step).
        lat?: number;
        lng?: number;
        accuracy?: number;
        quality?: CaptureQuality;
        locationNote?: string;
    };

    // She is still clocked in to another visit (the server refuses a second
    // open clock at a different home). Clock her out of that one from here,
    // with the location just captured for this clock-in, then clock in again.
    // The server records that visit's end as when her GPS trail shows she left
    // its home, if that was well before now.
    const clockOutOpenVisitThenClockIn = async (
        openVisitId: number,
        location: ClockLocation | undefined
    ) => {
        if (!id) {
            return;
        }
        setActionLoading(true);
        try {
            // The recorder and the GPS trail follow one visit at a time. Stop
            // them for the open visit (sending what they still hold) before this
            // visit's tracking starts, or its queued points are dropped.
            if (Recorder.state().visitId === openVisitId) {
                await Recorder.stop("cancelled").catch(() => {});
            }
            if (VisitLocationTrail.trackedVisitId() === openVisitId) {
                await VisitLocationTrail.stopAndFlush();
            }
            await clockOut(String(openVisitId), {
                lat: location?.lat,
                lng: location?.lng,
                accuracy: location?.accuracy,
                quality: location?.quality,
                locationNote: `Clocked out of this visit when clocking in to visit #${id}.`
            });
        } catch (e) {
            setError(e instanceof Error ? e.message : "Clock out failed");
            setActionLoading(false);
            return;
        }
        await handleClockIn(location);
    };

    const handleClockIn = async (location?: ClockLocation) => {
        if (!id) {
            return;
        }
        setActionLoading(true);
        try {
            const result = await clockIn(id, location);
            updateForm((prev) => ({
                ...prev,
                visit: {
                    ...prev.visit,
                    status: result.visit.status,
                    clockInAt: result.visit.clockInAt,
                    clockInLocation: result.visit.clockInLocation ?? null
                }
            }));
            // Visit Recording — DO NOT call Recorder.start here. The
            // auto-resume effect below is the single source of truth: it
            // fires when visit.status flips to "in_progress" (which
            // updateForm just did) and again on remount. Calling start here
            // too races the effect — both would proceed before
            // state.started flips, producing two POST /init calls and two
            // session rows per clock-in.
            setClockInModalVisible(false);
            // Auto-navigate to Assessment after clock-in
            setTimeout(() => {
                router.push(`/visit/${id}/assessment`);
            }, 300);
        } catch (e) {
            setClockInModalVisible(false);
            const open = extractOpenVisitConflict(e);
            if (open) {
                showDialog(
                    "You're still clocked in to another visit",
                    `${open.message}\n\nIf your phone shows you left that home earlier, ` +
                        `that time is recorded as the end of that visit. You can still ` +
                        `finish its paperwork afterwards.`,
                    [
                        { text: "Cancel", style: "cancel" },
                        {
                            text: "Clock out of it now",
                            onPress: () => clockOutOpenVisitThenClockIn(open.openVisitId, location)
                        }
                    ]
                );
            } else {
                setError(e instanceof Error ? e.message : "Clock in failed");
            }
        } finally {
            setActionLoading(false);
        }
    };

    // Clocked out already (from her next visit's door), paperwork now being
    // finished: the finish action submits only. No GPS, no clock-out modal: the
    // clock-out it has is kept.
    const paperworkOpenAfterClockOut = isClockedOutPaperworkOpen(visit);
    const handleSubmitAfterClockOut = async () => {
        if (!id) {
            return;
        }
        if (completedSections < totalSections) {
            setError("You already clocked out of this visit. Finish every section, then submit.");
            return;
        }
        setActionLoading(true);
        try {
            const result = await submitAndClockOut(id);
            updateForm((prev) => ({
                ...prev,
                visit: { ...prev.visit, status: result.visit.status }
            }));
        } catch (e) {
            setError(e instanceof Error ? e.message : "Submit failed");
        } finally {
            setActionLoading(false);
        }
    };

    const handleReportIssue = async (
        reason: ReportIssueReason,
        note: string | undefined,
        location?: { lat: number; lng: number }
    ) => {
        if (!id) {
            return;
        }
        setActionLoading(true);
        try {
            const result = await reportVisitIssue(id, { reason, note, location });
            updateForm((prev) => ({
                ...prev,
                visit: {
                    ...prev.visit,
                    status: result.visit.status,
                    clockInAt: result.visit.clockInAt,
                    clockInLocation: result.visit.clockInLocation ?? null,
                    onSiteIssue: result.visit.onSiteIssue ?? null
                }
            }));
            setIssueTicketId(result.ticketId);
            closeReportIssue();
            // Land the nurse straight in the chat with the manager bot.
            router.push(`/tickets/${result.ticketId}`);
        } catch (e) {
            setError(e instanceof Error ? e.message : "Could not report the issue");
            closeReportIssue();
        } finally {
            setActionLoading(false);
        }
    };

    const handleResumeIssue = async () => {
        if (!id) {
            return;
        }
        setActionLoading(true);
        try {
            const result = await resumeVisitIssue(id);
            updateForm((prev) => ({
                ...prev,
                visit: { ...prev.visit, status: result.visit.status, onSiteIssue: null }
            }));
        } catch (e) {
            setError(e instanceof Error ? e.message : "Could not resume the visit");
        } finally {
            setActionLoading(false);
        }
    };

    // When an issue is open but we don't have the ticket id (e.g. after a reload),
    // look up the visit's chat so "Open chat" can deep-link to it.
    useEffect(() => {
        if (!id || !visit.onSiteIssue || issueTicketId != null) {
            return;
        }
        let cancelled = false;
        void fetchRnTickets({ channel: "in_app_chat" })
            .then(({ data }) => {
                const match = data.find((t) => String(t.visitId) === String(id));
                if (!cancelled && match) {
                    setIssueTicketId(match.id);
                }
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [id, visit.onSiteIssue, issueTicketId]);

    const openIssueChat = () => {
        if (issueTicketId != null) {
            router.push(`/tickets/${issueTicketId}`);
        } else {
            router.push("/(tabs)/support");
        }
    };

    // Sent here by the supervisory form because the aide isn't there (D6a):
    // open the sheet on "Aide not here", once. With an issue already open or the
    // visit already missed, the banner covers it, so only the param is cleared.
    useEffect(() => {
        if (reportIssue !== "aide_not_here") {
            return;
        }
        router.setParams({ reportIssue: undefined });
        if (visit.onSiteIssue || visit.missedVisit) {
            return;
        }
        setReportIssueInitialReason("aide_not_here");
        setReportIssueModalVisible(true);
    }, [reportIssue, visit.onSiteIssue, visit.missedVisit]);

    const closeReportIssue = () => {
        setReportIssueModalVisible(false);
        setReportIssueInitialReason(null);
    };

    // Visit Recording — auto-resume on page mount if the visit is already
    // in progress (web reload, navigate-away-and-back). Recorder.start is
    // idempotent for the same visitId. Suppressed while an on-site issue is
    // open (she's blocked at the door, not recording a visit), and on a visit
    // that is over for her (marked missed, or clocked out), which can still
    // read `in_progress`.
    const captureLive = isVisitCaptureLive(visit);
    useEffect(() => {
        if (!id) {
            return;
        }
        if (visit.missedVisit) {
            // Marked missed while this screen was open: the visit is over, so
            // close what was capturing it. stop() flushes the trail's queue
            // first, so readings already taken still reach the server.
            if (Recorder.state().visitId === Number(id)) {
                void Recorder.stop("cancelled").catch((err) => {
                    console.warn("[recorder] stop on missed failed", err);
                });
            }
            if (VisitLocationTrail.trackedVisitId() === Number(id)) {
                VisitLocationTrail.stop();
            }
            return;
        }
        if (!captureLive) {
            return;
        }
        void Recorder.start(Number(id), {
            appVersion: Constants.expoConfig?.version ?? "unknown"
        }).catch((err) => {
            console.warn("[recorder] auto-resume failed", err);
        });
        // Visit location trail — same trigger, same idempotence. start() is a
        // no-op for a visit it is already tracking, so a remount does not
        // restart the 3-hour cap or take a duplicate reading. The ticker and
        // the app-state listener both live in app/_layout.tsx.
        VisitLocationTrail.start(Number(id));
    }, [id, captureLive, visit.missedVisit]);

    // Heartbeat lives in app/_layout.tsx (single global interval keyed on
    // Recorder.state().started). It is intentionally NOT here — pinning it
    // to this screen meant heartbeats stopped firing whenever the user
    // navigated to assessment.tsx, leaving the orphan-cleanup cron able to
    // server-cleanup an actively-recording session.

    // Unified terminal action — submit (if complete) + clock out. The modal
    // decides whether to require a note (incomplete) and captures GPS.
    const handleFinish = async (payload?: FinishPayload) => {
        if (!id) {
            return;
        }
        setActionLoading(true);
        try {
            const result = await submitAndClockOut(id, payload);
            updateForm((prev) => ({
                ...prev,
                visit: {
                    ...prev.visit,
                    // status is `post_processing` when submitted, else unchanged
                    // (`in_progress` + clockOutAt = the terminal incomplete case).
                    status: result.visit.status,
                    clockOutAt: result.visit.clockOutAt,
                    clockOutLocation: result.visit.clockOutLocation ?? null
                }
            }));
            // She's left the visit either way — close the recording session.
            try {
                await Recorder.stop("submit");
            } catch (err) {
                console.warn("[recorder] stop on finish failed", err);
            }
            // ...and stop the location trail, which flushes whatever is queued.
            VisitLocationTrail.stop();
            setClockOutModalVisible(false);
        } catch (e) {
            setError(e instanceof Error ? e.message : "Clock out failed");
            setClockOutModalVisible(false);
        } finally {
            setActionLoading(false);
        }
    };

    const age = computeAge(patient?.dob ?? null);

    return (
        <SafeAreaView style={styles.container} edges={["top"]}>
            {/* Back navigation */}
            <View style={styles.navBar}>
                <Pressable
                    onPress={() => {
                        if (router.canGoBack()) {
                            router.back();
                        } else {
                            router.replace("/(tabs)/visits");
                        }
                    }}
                    style={styles.backRow}
                >
                    <MaterialCommunityIcons name="chevron-left" size={22} color={colors.teal500} />
                    <Text style={styles.backText}>Back</Text>
                </Pressable>
            </View>

            <ScrollView
                contentContainerStyle={styles.scrollContent}
                showsVerticalScrollIndicator={false}
            >
                {/* 1. Header */}
                <VisitHeader
                    patientFirstName={patient?.firstName ?? ""}
                    patientLastName={patient?.lastName ?? ""}
                    age={age}
                    gender={patient?.gender ?? null}
                    language={patient?.language ?? null}
                />

                {/* 1a. Special / Ad-Hoc visit: read-only RN briefing up front (P5). */}
                {form.formSource === "special" && form.orientation ? (
                    <OrientationCard orientation={form.orientation} />
                ) : null}

                {/* 1b. Live Translation */}
                {visit?.id && visit.status === "in_progress" && !visit.missedVisit ? (
                    <LiveTranslationButton
                        visitId={visit.id}
                        patientLanguage={patient?.language ?? null}
                    />
                ) : null}

                {/* 2. Time / Clock / Payment */}
                <TimeClockPayment
                    visit={visit}
                    visitTypeName={form.visitType?.displayLabel ?? form.visitType?.name}
                    agencyName={form.agency?.publicName ?? form.agency?.name}
                />

                {isOpenForResubmission ? (
                    <View style={styles.resubmissionBanner}>
                        <View style={styles.bannerHeaderRow}>
                            <MaterialCommunityIcons
                                name="file-document-edit-outline"
                                size={20}
                                color={colors.teal600}
                            />
                            <Text style={[styles.bannerTitle, { color: colors.teal600 }]}>
                                Open for resubmission
                            </Text>
                        </View>
                        <Text style={styles.bannerText}>
                            {visit.resubmissionPurpose === "rn_clarification"
                                ? "Please answer the clarification questions about this visit and submit your answers."
                                : "Review the assessment, make the requested updates, and resubmit when finished."}
                        </Text>
                        <CorrectionQuestionsBadge visitId={id} />
                        {visit.resubmissionPurpose !== "rn_clarification" && (
                            <Pressable
                                style={[styles.bannerBtn, styles.bannerBtnPrimary]}
                                onPress={() => router.push(`/visit/${id}/assessment`)}
                            >
                                <Text style={styles.bannerBtnPrimaryText}>Open assessment</Text>
                            </Pressable>
                        )}
                    </View>
                ) : null}

                {/* 2b. On-site issue / missed banners */}
                {visit.missedVisit ? (
                    <View style={styles.missedBanner}>
                        <MaterialCommunityIcons
                            name="alert-circle-outline"
                            size={20}
                            color={missedVisitColors.accent}
                        />
                        <View style={styles.bannerBody}>
                            <Text style={[styles.bannerTitle, { color: missedVisitColors.accent }]}>
                                Visit marked missed
                            </Text>
                            <Text style={styles.bannerText}>
                                {visit.missedVisitReason ?? "This visit was marked as missed."}
                                {visit.missedVisitFault === "patient" ||
                                visit.missedVisitFault === "agency" ||
                                visit.missedVisitFault === "taskhealth"
                                    ? " Your travel time will be paid."
                                    : ""}
                            </Text>
                        </View>
                    </View>
                ) : visit.onSiteIssue ? (
                    <View style={styles.issueBanner}>
                        <View style={styles.bannerHeaderRow}>
                            <MaterialCommunityIcons
                                name="progress-clock"
                                size={20}
                                color={colors.warning500}
                            />
                            <Text style={[styles.bannerTitle, { color: colors.warning500 }]}>
                                Issue in progress
                            </Text>
                        </View>
                        <Text style={styles.bannerText}>
                            {getOnSiteIssueLabel(visit.onSiteIssue.reason)}: we&apos;re contacting
                            the{" "}
                            {visit.onSiteIssue.reason === "aide_not_here" ? "agency" : "patient"}.
                            Watch the chat for what to do next. We&apos;ll review what happened and
                            let you know about travel pay.
                        </Text>
                        <View style={styles.bannerActions}>
                            <Pressable
                                style={[styles.bannerBtn, styles.bannerBtnPrimary]}
                                onPress={openIssueChat}
                            >
                                <Text style={styles.bannerBtnPrimaryText}>Open chat</Text>
                            </Pressable>
                            <Pressable
                                style={[styles.bannerBtn, styles.bannerBtnSecondary]}
                                onPress={handleResumeIssue}
                                disabled={actionLoading}
                            >
                                <Text style={styles.bannerBtnSecondaryText}>
                                    I&apos;m in — resume visit
                                </Text>
                            </Pressable>
                        </View>
                    </View>
                ) : null}

                {/* 2c. Aide to evaluate — supervisory / performance-evaluation visits only */}
                {evaluatesAide ? <AideCard visitId={id ?? ""} visit={visit} /> : null}

                {/* 3. Address Section */}
                {patient ? <AddressSection patient={patient} visitId={id ?? ""} /> : null}

                {/* 4. Patient Contacts */}
                {patient ? (
                    <PatientContacts
                        visitId={id ?? ""}
                        patient={patient}
                        visit={visit}
                        includeCaregiver={!evaluatesAide}
                    />
                ) : null}

                {/* 5. Assessment Card — hidden while an open issue or miss blocks the visit */}
                {!visit.onSiteIssue && !visit.missedVisit ? (
                    <AssessmentButton
                        phases={phases}
                        sectionStatuses={sectionStatuses}
                        completedPhases={completedPhases}
                        totalPhases={phases.length}
                        completedSections={completedSections}
                        totalSections={totalSections}
                        onPress={() => router.push(`/visit/${id}/assessment`)}
                    />
                ) : null}

                {/* 5b. Patient context — intro + timeline, pre-visit and active */}
                {id && (visitState === 1 || visitState === 2) && !visit.missedVisit ? (
                    <PatientContextCard visitId={id} context={patientContext} />
                ) : null}
                {id ? <VisitDocumentsCard visitId={id} /> : null}

                {/* 6. Medical Info */}
                {patient ? (
                    <MedicalInfoCard
                        diagnoses={[]}
                        allergies={patient.allergies ?? null}
                        medications={[]}
                    />
                ) : null}

                {/* Report an issue — secondary action while the visit is active */}
                {!visit.onSiteIssue &&
                !visit.missedVisit &&
                !isOpenForResubmission &&
                (visitState === 1 || visitState === 2) ? (
                    <Pressable
                        style={styles.reportIssueBtn}
                        onPress={() => setReportIssueModalVisible(true)}
                        disabled={actionLoading}
                    >
                        <MaterialCommunityIcons
                            name="alert-outline"
                            size={18}
                            color={colors.textSecondary}
                        />
                        <Text style={styles.reportIssueText}>I can&apos;t start the visit</Text>
                    </Pressable>
                ) : null}
            </ScrollView>

            {/* 7. Sticky Bottom Button */}
            {!isOpenForResubmission ? (
                <StickyBottomButton
                    visitState={visitState}
                    onClockIn={() => setClockInModalVisible(true)}
                    onClockOut={() =>
                        paperworkOpenAfterClockOut
                            ? handleSubmitAfterClockOut()
                            : setClockOutModalVisible(true)
                    }
                    onViewPayment={() => router.push("/(tabs)/payments")}
                    disabled={actionLoading}
                    alreadyClockedOut={paperworkOpenAfterClockOut}
                />
            ) : null}

            {/* Clock In Modal */}
            {patient ? (
                <ClockInModal
                    visible={clockInModalVisible}
                    visit={visit}
                    patient={patient}
                    onConfirm={handleClockIn}
                    onCancel={() => setClockInModalVisible(false)}
                />
            ) : null}

            {/* Report Issue Modal */}
            <ReportIssueModal
                visible={reportIssueModalVisible}
                initialReason={reportIssueInitialReason}
                onConfirm={handleReportIssue}
                onCancel={closeReportIssue}
            />

            {/* Clock Out Modal */}
            <ClockOutModal
                visible={clockOutModalVisible}
                visit={visit}
                patient={patient}
                completedSections={completedSections}
                totalSections={totalSections}
                onConfirm={handleFinish}
                onCancel={() => setClockOutModalVisible(false)}
                onReturnToAssessment={() => {
                    setClockOutModalVisible(false);
                    router.push(`/visit/${id}/assessment`);
                }}
            />

            {/* Error snackbar */}
            <Snackbar
                visible={!!error}
                onDismiss={() => setError(null)}
                duration={3000}
                style={styles.snackbar}
            >
                {error}
            </Snackbar>
            <ConfirmDialog {...dialogState} onDismiss={dismissDialog} />
        </SafeAreaView>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: colors.background
    },
    navBar: {
        paddingHorizontal: spacing.base,
        paddingTop: spacing.sm,
        paddingBottom: spacing.md,
        backgroundColor: colors.surface,
        borderBottomWidth: 1,
        borderBottomColor: colors.border
    },
    backRow: {
        flexDirection: "row",
        alignItems: "center",
        marginLeft: -spacing.xs
    },
    backText: {
        fontFamily: fonts.semiBold,
        fontSize: 14,
        color: colors.teal500
    },
    pageTitle: {
        fontFamily: fonts.bold,
        fontSize: 18,
        color: colors.textPrimary,
        marginTop: spacing.xs
    },
    scrollContent: {
        paddingBottom: 100
    },
    snackbar: {
        backgroundColor: colors.error500,
        marginBottom: spacing.xl
    },
    issueBanner: {
        marginHorizontal: spacing.base,
        marginTop: spacing.md,
        padding: spacing.base,
        borderRadius: radius.lg,
        borderWidth: 1,
        borderColor: colors.warning500,
        backgroundColor: "#FFF8EC"
    },
    missedBanner: {
        flexDirection: "row",
        gap: spacing.sm,
        marginHorizontal: spacing.base,
        marginTop: spacing.md,
        padding: spacing.base,
        borderRadius: radius.lg,
        borderWidth: 1,
        borderColor: missedVisitColors.accent,
        backgroundColor: missedVisitColors.background
    },
    resubmissionBanner: {
        marginHorizontal: spacing.base,
        marginTop: spacing.md,
        padding: spacing.base,
        borderRadius: radius.lg,
        borderWidth: 1,
        borderColor: colors.teal400,
        backgroundColor: colors.teal50
    },
    bannerBody: {
        flex: 1
    },
    bannerHeaderRow: {
        flexDirection: "row",
        alignItems: "center",
        gap: spacing.sm,
        marginBottom: spacing.xs
    },
    bannerTitle: {
        fontFamily: fonts.bold,
        fontSize: 14
    },
    bannerText: {
        fontFamily: fonts.regular,
        fontSize: 13,
        color: colors.textSecondary,
        lineHeight: 18
    },
    bannerActions: {
        flexDirection: "row",
        gap: spacing.sm,
        marginTop: spacing.md
    },
    bannerBtn: {
        flex: 1,
        height: 42,
        borderRadius: radius.full,
        alignItems: "center",
        justifyContent: "center"
    },
    bannerBtnPrimary: {
        backgroundColor: colors.teal500
    },
    bannerBtnPrimaryText: {
        fontFamily: fonts.bold,
        fontSize: 13,
        color: "#FFFFFF"
    },
    bannerBtnSecondary: {
        borderWidth: 1,
        borderColor: colors.border,
        backgroundColor: colors.surface
    },
    bannerBtnSecondaryText: {
        fontFamily: fonts.semiBold,
        fontSize: 13,
        color: colors.textPrimary
    },
    reportIssueBtn: {
        flexDirection: "row",
        alignItems: "center",
        justifyContent: "center",
        gap: spacing.xs,
        marginHorizontal: spacing.base,
        marginTop: spacing.md,
        paddingVertical: spacing.base,
        borderWidth: 1,
        borderColor: colors.border,
        borderRadius: radius.full,
        backgroundColor: "transparent"
    },
    reportIssueText: {
        fontFamily: fonts.semiBold,
        fontSize: 14,
        color: colors.textSecondary
    }
});
